// Port of load_bmo / _bmo_account_label_from_filename in scripts/loaders.py.
// Behavior must match: same row counts, same sign convention (expenses positive),
// same account labels, same inter-portal-transfer filter, same id hashes.

import {
  type Transaction,
  type AccountKind,
  newTransaction,
  makeId,
  parseDate,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
  extractLast4,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

export function bmoAccountLabelFromFilename(filename: string): string {
  const name = filename.toLowerCase();
  if (/mastercard|[_\-]mc[_\-.]|^mc[_\-.]/.test(name)) return "BMO Mastercard";
  if (/chequing|checking|[_\-]chq[_\-.]|^chq[_\-.]/.test(name)) return "BMO Chequing";
  return "BMO";
}

export async function loadBmo(text: string, filename: string): Promise<Transaction[]> {
  const allRows = parseCsv(text);
  const headerIdx = findHeaderIndex(allRows, ["Transaction Amount", "Description"]);
  if (headerIdx < 0) return [];

  // Normalize header keys by stripping whitespace — some BMO exports ship a
  // leading space in the header line (e.g. " Transaction Amount").
  const header = allRows[headerIdx].map((h) => (h || "").trim());
  const dataRows = allRows.slice(headerIdx + 1);
  const dictRows = toDictRows(header, dataRows);
  const accountLabel = bmoAccountLabelFromFilename(filename);

  const out: Transaction[] = [];
  for (const row of dictRows) {
    const cardRaw =
      row["First Bank Card"] ||
      row["First Bank Account"] ||
      row["Card"] ||
      "";
    const card = cardRaw.replace(/^'/, "").trim();

    const rawDate = (row["Date Posted"] || row["Transaction Date"] || "").trim();
    const rawAmount = (row["Transaction Amount"] || "").trim();
    // Skip blank padding rows that BMO inserts between preamble and data.
    if (!rawDate && !rawAmount) continue;

    const dateStr = parseDate(rawDate);
    // BMO source convention: negative = money out. Flip so expenses are positive.
    const amount = -parseAmount(rawAmount || "0");
    const description = (row["Description"] || "").trim().replace(/^"|"$/g, "");

    if (isInterPortalTransfer(description)) continue;

    // BMO Mastercard → liability; chequing/savings → debit; ambiguous "BMO"
    // → leave blank so downstream consumers fall back to per-tx heuristics.
    const accountKind: AccountKind =
      accountLabel === "BMO Mastercard" ? "liability" :
      accountLabel === "BMO Chequing" ? "debit" : "";

    const id = await makeId("bmo", accountLabel, dateStr, amount, description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: description,
        vendor_key: normalizeVendor(description),
        amount: Math.round(amount * 100) / 100,
        portal: "bmo",
        account: accountLabel,
        account_kind: accountKind,
        account_last4: extractLast4(card),
        source_file: filename,
        raw: { ...row },
      }),
    );
  }
  return out;
}
